// Keeps the floating chat widgets clear of the bottom bars (the sticky Buy now bar, the combo tray and the
// cart's Check out bar) and of each other:
// - Microsoft Clarity's Brand Agent: its widget is pinned to the bottom of the screen, so it covered Buy now.
//   It is lifted by the height of whichever bar is showing (--bottom-bar-height) and keeps its own gap.
// - The WhatsUp "Chat on WhatsApp" button: bottom = the larger of --floating-offset (base.css: 24px desktop,
//   96px phones, plus the safe area) and --floating-clearance, measured from whatever sits below it in its
//   columns, the agent's widget included.
// Both apps draw inside open shadow roots, so one style rule is added in each. If an app changes its markup
// the rule simply stops matching.
(function () {
  if (window.floatingChat) return;
  window.floatingChat = true;

  const BARS = '.sticky-buy.is-visible, .combo-summary, #main-cart-footer:not(.is-empty) .cart__ctas';
  const GAP = 12;
  const root = document.documentElement;
  let button;
  let ticking = false;

  const addStyle = (shadow, css) => {
    if (!shadow || shadow.querySelector('style[data-floating-chat]')) return false;
    const style = document.createElement('style');
    style.dataset.floatingChat = '';
    style.textContent = css;
    shadow.append(style);
    return true;
  };

  const agentShadow = () => document.getElementById('ads-agent-host')?.shadowRoot;

  const measure = () => {
    ticking = false;
    const height = window.innerHeight;

    // 1. The tallest full-width bar along the bottom; the agent's widget sits above it
    let bar = 0;
    document.querySelectorAll(BARS).forEach((element) => {
      if (getComputedStyle(element).position !== 'fixed') return;
      const box = element.getBoundingClientRect();
      if (box.height && box.top > height * 0.5) bar = Math.max(bar, height - box.top);
    });
    root.style.setProperty('--bottom-bar-height', `${Math.round(bar)}px`);

    // 2. The WhatsApp button clears the bars and the agent's widget in its own columns
    if (!button) return;
    const own = button.getBoundingClientRect();
    const obstacles = [...document.querySelectorAll(BARS)];
    agentShadow()?.querySelectorAll('*').forEach((element) => obstacles.push(element));
    let clearance = 0;
    obstacles.forEach((element) => {
      if (getComputedStyle(element).position !== 'fixed') return;
      const box = element.getBoundingClientRect();
      if (!box.height || box.right <= own.left || box.left >= own.right) return;
      if (box.top < height * 0.5) return; // only things anchored to the bottom half
      clearance = Math.max(clearance, height - box.top + GAP);
    });
    // Never climb past the middle of the screen
    clearance = Math.min(clearance, Math.round(height * 0.45));
    root.style.setProperty('--floating-clearance', `${Math.round(clearance)}px`);
  };

  const request = () => {
    if (ticking) return;
    ticking = true;
    requestAnimationFrame(measure);
  };

  // Widgets animate as they move and open, so measure now and again once the animation has settled
  let settle;
  const requestTwice = () => {
    request();
    clearTimeout(settle);
    settle = setTimeout(request, 400);
  };

  // The Brand Agent arrives late; lift its widget and watch it for size changes whenever it appears
  let agentWatched = false;
  const watchAgent = () => {
    const shadow = agentShadow();
    if (!shadow || agentWatched) return;
    agentWatched = true;
    addStyle(
      shadow,
      `.fixed.bottom-0 {
        bottom: var(--bottom-bar-height, 0px) !important;
      }`
    );
    new MutationObserver(requestTwice).observe(shadow, { childList: true, subtree: true, attributes: true });
    requestTwice();
  };

  const watchWhatsApp = () => {
    const shadow = document.querySelector('whatsup-whatsapp-button')?.shadowRoot;
    const found = shadow?.querySelector('.whatsup-whatsapp-button');
    if (!found) return;
    button = found;
    addStyle(
      shadow,
      `.whatsup-whatsapp-button {
        bottom: max(var(--floating-offset, 24px), var(--floating-clearance, 0px)) !important;
        transition: transform 0.3s cubic-bezier(0.075, 0.82, 0.165, 1), bottom 200ms cubic-bezier(0.23, 1, 0.32, 1);
      }
      @media (prefers-reduced-motion: reduce) {
        .whatsup-whatsapp-button { transition: none; }
      }`
    );
    requestTwice();
  };

  const start = () => {
    window.addEventListener('scroll', request, { passive: true });
    window.addEventListener('resize', request, { passive: true });
    // Bars show and hide (class changes); the agent's host is added to <body> when it loads
    new MutationObserver(requestTwice).observe(document.body, { attributes: true, attributeFilter: ['class'], subtree: true });
    new MutationObserver(() => {
      watchAgent();
      requestTwice();
    }).observe(document.body, { childList: true });
    watchAgent();
    customElements.whenDefined('whatsup-whatsapp-button').then(() => requestAnimationFrame(watchWhatsApp));
    window.addEventListener('load', () => setTimeout(requestTwice, 1500));
    requestTwice();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
